import { useTranslation } from 'react-i18next'
import type { TextSize } from '@/generated/graphql'

interface TextPart {
  number: number
  text: string
  size: TextSize
}

interface FeaturedCardProps {
  description?: string | null
  imageUrl: string
  content: TextPart[]
  onOpen: () => void
}

const FEATURED_TEXT_SIZE: Record<string, string> = {
  SMALL: 'text-[12px] font-semibold',
  STANDARD: 'text-[12px] font-semibold',
  BIG: 'text-[24px] font-semibold',
}

export function FeaturedCard({
  description,
  imageUrl,
  content,
  onOpen,
}: FeaturedCardProps) {
  const { t } = useTranslation()
  const sorted = [...content].sort((a, b) => a.number - b.number)

  return (
    <button
      type="button"
      onClick={onOpen}
      className="@container relative flex h-[220px] w-full cursor-pointer overflow-hidden rounded-[24px] bg-app-primary-600 text-left transition-transform duration-200 ease-out hover:scale-[1.02]"
    >
      {/* Left content */}
      <div className="flex flex-1 flex-col p-[16px] @min-[400px]:p-[24px] @min-[400px]:max-w-[40%]">
        <span
          className="w-fit rounded-full bg-white/5 px-[12px] py-[4px] text-[12px] font-semibold text-white"
          style={{ letterSpacing: '0.24px' }}
        >
          {t('discover_featured_tag')}
        </span>

        {description && (
          <p className="mt-[8px] pr-[24px] text-[14px] font-semibold leading-[20px] text-white">
            {description}
          </p>
        )}

        <div className="flex-1" />

        <span
          className="text-[12px] font-semibold text-white"
          style={{ letterSpacing: '0.24px' }}
        >
          {t('discover_feature_button_text')}
        </span>
      </div>

      {/* Right — image card with text overlay */}
      <div className="ml-auto flex items-center pr-[16px] @min-[400px]:pr-[24px]">
        <div
          className="relative h-[186px] w-[132px] overflow-hidden rounded-[32px] shadow-lg"
          style={{ transform: 'rotate(4deg)' }}
        >
          <img
            src={imageUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-300"
            onLoad={(e) => e.currentTarget.classList.remove('opacity-0')}
          />
          <div className="relative flex h-full w-full flex-col items-center justify-center gap-[2px] p-[12px]">
            {sorted.map((part) => (
              <span
                key={part.number}
                className={`w-full text-center text-app-grey-900 hyphens-auto ${FEATURED_TEXT_SIZE[part.size] ?? 'text-xs'}`}
                style={{ overflowWrap: 'anywhere' }}
              >
                {part.text}
              </span>
            ))}
          </div>
        </div>
      </div>
    </button>
  )
}
